import { useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function SitemapPage() {
  const [activeTab, setActiveTab] = useState('xml');
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(200);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setResult(null);
    setLoading(true);
    setStatus(`Crawling and generating ${activeTab.toUpperCase()} sitemap...`);
    try {
      const endpoint = activeTab === 'xml' ? '/html-sitemap/generate-xml' : '/html-sitemap/generate';
      const { data } = await api.post(endpoint, { url, max_pages: maxPages });
      setResult(data);
      setStatus('Generated successfully!');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    const text = result?.xml || result?.html || '';
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${activeTab.toUpperCase()} copied`);
  };

  const uploadToSite = async () => {
    if (!result?.xml) return;
    setUploading(true);
    try {
      const { data } = await api.post('/html-sitemap/apply', { xml: result.xml, url });
      toast.success('Sitemap uploaded to your connected site.');
      const warning = data?.detail?.warning;
      if (warning) toast.warning(warning, { duration: 10000 });
    } catch (err) {
      toast.error(errDetail(err));
    } finally {
      setUploading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const isXml = activeTab === 'xml';
    const content = isXml ? result.xml : result.html;
    const blob = new Blob([content], { type: isXml ? 'application/xml' : 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = isXml ? 'sitemap.xml' : 'sitemap.html';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sitemap Generator</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Crawl the site and generate XML or HTML sitemaps.</p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'xml', label: 'XML Sitemap' },
          { id: 'html', label: 'HTML Sitemap' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => { setActiveTab(t.id); setResult(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleGenerate} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target URL</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Max Pages ({maxPages})</label>
          <input
            type="range" min="20" max="500" step="20"
            value={maxPages}
            onChange={(e) => setMaxPages(parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60"
        >
          {loading ? 'Generating...' : `Generate ${activeTab.toUpperCase()} Sitemap`}
        </button>

        {status && (
          <div className={`p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}
      </form>

      {result && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Total URLs:</span> <span className="font-medium">{result.total_urls ?? result.total_pages}</span></div>
            {result.size_bytes != null && <div><span className="text-slate-500">Size:</span> <span className="font-medium">{(result.size_bytes / 1024).toFixed(1)} KB</span></div>}
            {result.sections && Object.entries(result.sections).map(([k, v]) => (
              <div key={k}><span className="text-slate-500">{k}:</span> <span className="font-medium">{v}</span></div>
            ))}
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={copy} className="text-sm px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
              Copy
            </button>
            <button type="button" onClick={download} className="text-sm px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700">
              Download
            </button>
            {activeTab === 'xml' && (
              <button
                type="button"
                onClick={uploadToSite}
                disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-md border border-violet-600 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40 disabled:opacity-60"
              >
                {uploading ? 'Uploading...' : 'Upload to connected site'}
              </button>
            )}
          </div>

          <pre className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md overflow-auto max-h-96 text-xs whitespace-pre-wrap text-slate-700 dark:text-slate-300">
            {result.xml || result.html}
          </pre>
        </div>
      )}
    </motion.div>
  );
}
